import type { WithActiveBranchId } from '@app/api/shared';
import type { NActionsHistory, NEmployees, NUsers } from '@app/types';

export enum UsersEndpoints {
  GetUserSocialIntegrations = 'GetUserSocialIntegrations',
  AddUserSocialIntegrations = 'AddUserSocialIntegrations',
  RemoveUserSocialIntegrations = 'RemoveUserSocialIntegrations',
  GetUserSocialIntegrationsForAddition = 'GetUserSocialIntegrationsForAddition',
  GetOrganizationUsers = 'GetOrganizationUsers',
  GetEmployeesList = 'GetEmployeesList',
  SearchUsers = 'SearchUsers',
  GetUsersSearchResult = 'GetUsersSearchResult',
  SearchEmployees = 'SearchEmployees',
  GetEmployeesSearchResult = 'GetEmployeesSearchResult',
  GetBranchEmployees = 'GetBranchEmployees',
  ExportEmployeesToExcel = 'ExportEmployeesToExcel',
  ImportExcel = 'ImportExcel',
  StopImportExcel = 'StopImportExcel',
  GetUserHistoryFilterData = 'GetUserHistoryFilterData',
  SearchUserHistory = 'SearchUserHistory',
  GetUserHistorySearchResult = 'GetUserHistorySearchResult',
  GetUsersFilterData = 'GetUsersFilterData',
  GetUsersByIds = 'GetUsersByIds',
  GetEmployeesWithoutCurrentBranch = 'GetEmployeesWithoutCurrentBranch',
  ExportEmployeesToExcelByIds = 'ExportEmployeesToExcelByIds',
  GetBranchEmployeesForSystem = 'GetBranchEmployeesForSystem',
  GetClientsFilterData = 'GetClientsFilterData',
}

export interface ModelSaleFunnel {
  Id: number;
  Name: string;
  Description: string | null;
}

export interface ModelSaleFunnelStage {
  Id: number;
  Name: string;
  Description: string | null;
  Color: string | null;
}

export interface ModelUserIntegration {
  Id: number;
  TimeCreated: string | null;
  BranchId: number;
  BranchName: string | null;
  SocialType: string | null;
  AccountLogin: string | null;
  AccountName: string | null;
  IntegrationName: string | null;
  Status: string | null;
  IsDeleted: boolean;
  Avatar: string | null;
  ConsumeHistoryFrom: string | null;
  Token: string | null;
  SalesFunnel: ModelSaleFunnel;
  SalesFunnelStage: ModelSaleFunnelStage;
}

export interface ModelOrganizationUser {
  Id: number;
  IdS: string | null;
  Name: string | null;
}

export interface ModelEmployeeLite {
  Id: number;
  NameFirst: string | null;
  NameLast: string | null;
  NameFull: string | null;
  Gender: string | null;
  PositionId: number | null;
  Position: string | null;
  IsDeleted: boolean;
  IsTerminated: boolean;
  CountClientServices: number;
  WorkScheduleTill: string | null;
  IsBelongToCurrentBranch: boolean;
}

export type GetUserSocialIntegrationsRequest = WithActiveBranchId<{
  forUserId: number;
}>;

export interface UserSocialIntegrationsPayload {
  UserId: number;
  IntegrationIds: number[] | null;
}

export type AddUserSocialIntegrationsRequest = WithActiveBranchId<{
  data: UserSocialIntegrationsPayload;
}>;

export type RemoveUserSocialIntegrationsRequest = WithActiveBranchId<{
  data: UserSocialIntegrationsPayload;
}>;

export type GetUserSocialIntegrationsForAdditionRequest = WithActiveBranchId<{
  forUserId: number;
}>;

export interface GetOrganizationUsersRequest {
  databaseId?: string;
  ActiveBranchId?: number | null;
}

export interface GetEmployeesListRequest {
  ActiveBranchId: number | null;
}

export type GetUserSocialIntegrationsResponse = ModelUserIntegration[];
export type AddUserSocialIntegrationsResponse = ModelUserIntegration[];
export type RemoveUserSocialIntegrationsResponse = ModelUserIntegration[];
export type GetUserSocialIntegrationsForAdditionResponse =
  ModelUserIntegration[];
export type GetOrganizationUsersResponse = ModelOrganizationUser[];
export type GetEmployeesListResponse = ModelEmployeeLite[];

export interface SearchUsersRequest {
  ActiveBranchId: number | null;
  data: NUsers.ModelUserSearchFilter;
}

export interface GetUsersSearchResultRequest {
  // Id поиска может превышать Number.MAX_SAFE_INTEGER, поэтому передаётся строкой,
  // чтобы не терять точность при обратном преобразовании в number.
  searchId: string;
  page: number;
  rowsPerPage: number;
  ActiveBranchId: number | null;
}

export interface SearchEmployeesRequest {
  ActiveBranchId: number | null;
  data: Omit<NEmployees.ModelEmployeeSearchFilter, 'IsTerminated'> & {
    IsTerminated?: NEmployees.ModelEmployeeSearchFilter['IsTerminated'];
  };
}

export interface GetEmployeesSearchResultRequest {
  searchId: number;
  page: number;
  rowsPerPage: number;
  ActiveBranchId: number | null;
}

export interface GetBranchEmployeesRequest {
  branchId: number;
  ActiveBranchId: number | null;
  /**
   * Вернуть вместе с исполнителями и остальных пользователей филиала.
   * Нужен графику работы: там показываются все пользователи, а журнал
   * записей по-прежнему запрашивает только исполнителей.
   */
  includeUsers?: boolean;
  logs?: {
    isLogged: boolean;
    userId: number | null;
    database: string | null;
  };
}

export type SearchUsersResponse = NUsers.ModelUserSearchResult;
export type GetUsersSearchResultResponse = NUsers.ModelUserSearchResult;
export type SearchEmployeesResponse = NEmployees.ModelEmployeeSearchResult;
export type GetEmployeesSearchResultResponse =
  NEmployees.ModelEmployeeSearchResult;
export type GetBranchEmployeesResponse = NEmployees.ModelEmployee[];

export interface UsersModelFile {
  Id: number;
  Name: string | null;
  Data: string | null;
  Lifetime: string | null;
  Description: string | null;
}

export interface UsersModelItem {
  Id: number;
  IdS: string | null;
  Name: string | null;
}

export interface ExportEmployeesToExcelRequest {
  searchId: number;
  ActiveBranchId: number | null;
}

export interface ImportExcelRequest {
  extension: string;
  ActiveBranchId: number | null;
  data: string;
}

export interface StopImportExcelRequest {
  ActiveBranchId: number | null;
}

export interface GetUserHistoryFilterDataRequest {
  ActiveBranchId: number | null;
  logs?: {
    isLogged: boolean;
    userId: number | null;
    database: string | null;
  };
}

export interface SearchUserHistoryRequest {
  ActiveBranchId: number | null;
  data: NActionsHistory.ModelSearchUserHistoryFilter;
}

export interface GetUserHistorySearchResultRequest {
  searchId: number;
  page: number;
  itemsPerPage: number;
  ActiveBranchId: number | null;
}

export interface GetUsersFilterDataRequest {
  ActiveBranchId: number | null;
}

export interface GetUsersByIdsRequest {
  ActiveBranchId: number | null;
  data: number[];
}

export interface GetEmployeesWithoutCurrentBranchRequest {
  ActiveBranchId: number | null;
}

export interface ExportEmployeesToExcelByIdsRequest {
  ActiveBranchId: number | null;
  data: number[] | null;
}

export interface GetBranchEmployeesForSystemRequest {
  branchId: number;
  ActiveBranchId: number | null;
}

export interface UsersGetClientsFilterDataRequest {
  ActiveBranchId: number | null;
}

export type ExportEmployeesToExcelResponse = UsersModelFile;
export type ExportEmployeesToExcelByIdsResponse = UsersModelFile;
export type ImportExcelResponse = void;
export type StopImportExcelResponse = void;
export type GetUserHistoryFilterDataResponse =
  NActionsHistory.UserHistoryFilterData;
export type SearchUserHistoryResponse =
  NActionsHistory.ModelSearchUserHistoryResult;
export type GetUserHistorySearchResultResponse =
  NActionsHistory.ModelSearchUserHistoryResult;
export type GetUsersFilterDataResponse = {
  Branches: { Id: number; Name: string | null }[] | null;
  Positions: { Id: number; Name: string | null }[] | null;
  Statuses: string[] | null;
};
export type GetUsersByIdsResponse = NUsers.ModelUser[];
export type GetEmployeesWithoutCurrentBranchResponse =
  NEmployees.ModelEmployee[];
export type GetBranchEmployeesForSystemResponse = UsersModelItem[];
export type UsersGetClientsFilterDataResponse = ModelEmployeeLite[];
